import type { HTMLAttributes, ReactNode, TdHTMLAttributes } from 'react'
import s from './ui.module.css'

/* The small presentational primitives. None of them fetches, and none reaches
   into a store — that rule is what keeps them reusable across both portals. */

/* ── Card ────────────────────────────────────────────────────────────────── */
export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  padded?: boolean
  interactive?: boolean
}

export function Card({
  padded = true,
  interactive = false,
  className,
  children,
  ...rest
}: CardProps): ReactNode {
  return (
    <div
      className={[s.card, padded ? s.cardPad : '', interactive ? s.cardInteractive : '', className]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    >
      {children}
    </div>
  )
}

/* ── Badge ───────────────────────────────────────────────────────────────── */
export type BadgeTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'primary'

const badgeTones: Record<BadgeTone, string> = {
  neutral: s.badgeNeutral!,
  success: s.badgeSuccess!,
  warning: s.badgeWarning!,
  danger: s.badgeDanger!,
  info: s.badgeInfo!,
  primary: s.badgePrimary!,
}

export function Badge({
  tone = 'neutral',
  children,
  className,
  ...rest
}: { tone?: BadgeTone } & HTMLAttributes<HTMLSpanElement>): ReactNode {
  return (
    <span className={[s.badge, badgeTones[tone], className].filter(Boolean).join(' ')} {...rest}>
      {children}
    </span>
  )
}

/* ── Alert ───────────────────────────────────────────────────────────────── */
export type AlertTone = 'info' | 'success' | 'warning' | 'danger'

const alertTones: Record<AlertTone, string> = {
  info: s.alertInfo!,
  success: s.alertSuccess!,
  warning: s.alertWarning!,
  danger: s.alertDanger!,
}

export function Alert({
  tone = 'info',
  title,
  children,
  className,
  ...rest
}: { tone?: AlertTone; title?: ReactNode } & HTMLAttributes<HTMLDivElement>): ReactNode {
  return (
    <div
      className={[s.alert, alertTones[tone], className].filter(Boolean).join(' ')}
      role={tone === 'danger' ? 'alert' : 'status'}
      {...rest}
    >
      <div className={s.alertBody}>
        {title !== undefined && <div className={s.alertTitle}>{title}</div>}
        {children}
      </div>
    </div>
  )
}

/* ── Table ───────────────────────────────────────────────────────────────
   Always inside its own horizontal scroll container: a wide table must never
   make the page body scroll sideways. */
export function Table({
  children,
  className,
  caption,
  ...rest
}: { caption?: string } & HTMLAttributes<HTMLTableElement>): ReactNode {
  return (
    <div className={s.tableWrap}>
      <table className={[s.table, className].filter(Boolean).join(' ')} {...rest}>
        {caption !== undefined && <caption className="visually-hidden">{caption}</caption>}
        {children}
      </table>
    </div>
  )
}

export function Td({ children, ...rest }: TdHTMLAttributes<HTMLTableCellElement>): ReactNode {
  return <td {...rest}>{children}</td>
}

/* ── Tabs ────────────────────────────────────────────────────────────────── */
export interface TabsProps<T extends string> {
  value: T
  onChange: (value: T) => void
  options: { value: T; label: ReactNode; count?: number }[]
  label: string
}

export function Tabs<T extends string>({
  value,
  onChange,
  options,
  label,
}: TabsProps<T>): ReactNode {
  return (
    <div className={s.tabs} role="tablist" aria-label={label}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="tab"
          aria-selected={option.value === value}
          className={[s.tab, option.value === value ? s.tabActive : ''].filter(Boolean).join(' ')}
          onClick={() => onChange(option.value)}
        >
          {option.label}
          {option.count !== undefined && ` (${option.count})`}
        </button>
      ))}
    </div>
  )
}

/* ── Skeleton ────────────────────────────────────────────────────────────── */
export function Skeleton({
  width = '100%',
  height = '1em',
  className,
}: {
  width?: string | number
  height?: string | number
  className?: string
}): ReactNode {
  return (
    <div
      className={[s.skeleton, className].filter(Boolean).join(' ')}
      style={{ width, height }}
      aria-hidden="true"
    />
  )
}

export function SkeletonList({ rows = 3 }: { rows?: number }): ReactNode {
  return (
    <div aria-busy="true" aria-live="polite">
      <span className="visually-hidden">Loading…</span>
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} height="72px" className={s.cardPad} />
      ))}
    </div>
  )
}

/* ── EmptyState ──────────────────────────────────────────────────────────── */
export function EmptyState({
  title,
  children,
  action,
}: {
  title: ReactNode
  children?: ReactNode
  action?: ReactNode
}): ReactNode {
  return (
    <div className={s.empty}>
      <p className={s.emptyTitle}>{title}</p>
      {children !== undefined && <div className={s.emptyBody}>{children}</div>}
      {action}
    </div>
  )
}

/* ── Pagination ──────────────────────────────────────────────────────────── */
export function Pagination({
  shown,
  hasMore,
  loading,
  onMore,
}: {
  shown: number
  hasMore: boolean
  loading: boolean
  onMore: () => void
}): ReactNode {
  return (
    <div className={s.pagination}>
      <span className={s.paginationInfo}>
        Showing {shown} {shown === 1 ? 'row' : 'rows'}
        {hasMore ? '' : ' · end of list'}
      </span>
      {hasMore && (
        <button type="button" className={[s.button, s.secondary].join(' ')} onClick={onMore} disabled={loading}>
          {loading ? 'Loading…' : 'Load more'}
        </button>
      )}
    </div>
  )
}
